import { api, Epic, TaskItem, TaskComment } from '../api';
import { renderMarkdown } from '../lib/markdown';
import { statusMeta } from '../lib/status';
import { escapeHtml, formatDate, initials } from '../lib/utils';
import { flattenTree } from '../lib/taskTree';
import { openModal } from '../components/modal';
import { openTaskModal } from '../components/taskModal';
import { openTaskForm } from '../components/taskForm';
import { toast } from '../components/toast';
import { ProjectContext } from './project';

export async function renderEpicDetail(
  content: HTMLElement,
  ctx: ProjectContext,
  epicId: string
): Promise<void> {
  const { project, reload, readOnly, canComment } = ctx;

  let epic: Epic;
  try {
    const res = await api.get<{ epic: Epic }>(`/projects/${project.id}/epics/${epicId}`);
    epic = res.epic;
  } catch (e: any) {
    content.innerHTML = `
      <div class="error-state">
        <h3>Epic not available</h3>
        <p class="muted">${escapeHtml(e.message)}</p>
        <button class="btn btn-primary" data-back>Back to Epics</button>
      </div>
    `;
    content.querySelector('[data-back]')!.addEventListener('click', () =>
      ctx.goEpics()
    );
    return;
  }

  const epicTasks = ctx.tasks.filter((t) => t.epicId === epic.id);
  const done = epicTasks.filter((t) => t.status === 'complete');
  const total = epicTasks.length;
  const completed = done.length;
  const pointsDone = done.reduce((s, t) => s + t.storyPoints, 0);
  const totalPoints = epicTasks.reduce((s, t) => s + t.storyPoints, 0);
  const percent = total === 0 ? 0 : Math.round((completed / total) * 100);

  content.innerHTML = `
    <div style="margin-bottom:20px;">
      <a class="nav-link" style="padding:0;display:inline-block;margin-bottom:12px;" href="#${ctx.projectHref('epics')}">← Back to Epics</a>
      <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px;flex-wrap:wrap;">
        <div>
          <h1 style="font-size:24px;">${escapeHtml(epic.name)}</h1>
          ${
            epic.description
              ? `<div class="markdown-body" style="border:none;padding:0;max-width:720px;">${renderMarkdown(epic.description)}</div>`
              : '<p class="muted">No description yet.</p>'
          }
        </div>
        ${
          readOnly
            ? '<span class="muted" style="font-size:13px;">Read-only view</span>'
            : `<div style="display:flex;gap:10px;">
          <button class="btn btn-ghost" id="edit-epic">Edit</button>
          <button class="btn btn-danger" id="delete-epic">Delete</button>
        </div>`
        }
      </div>
    </div>

    <div class="stat-grid" style="grid-template-columns:repeat(4,1fr);">
      <div class="stat-card">
        <div class="stat-value">${total}</div>
        <div class="stat-label">Total tasks</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">${completed}</div>
        <div class="stat-label">Completed</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">${percent}%</div>
        <div class="stat-label">Completion</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">${pointsDone}<span style="font-size:16px;color:var(--text-3);"> / ${totalPoints}</span></div>
        <div class="stat-label">Story points done</div>
      </div>
    </div>

    <div class="chart-grid" style="margin-bottom:24px;">
      <div class="chart-card">
        <h3>Completion</h3>
        <div class="chart-box"><canvas id="epic-completion-chart"></canvas></div>
      </div>
      <div class="chart-card">
        <h3>Tasks by status</h3>
        <div class="chart-box"><canvas id="epic-status-chart"></canvas></div>
      </div>
    </div>

    <div class="list-toolbar">
      <h3 style="margin:0;font-size:16px;">Tasks in this epic</h3>
      ${readOnly ? '' : '<button class="btn btn-primary" id="add-task">+ New Task</button>'}
    </div>
    <div id="epic-task-list">${renderEpicTasks(epicTasks, ctx)}</div>
    <div id="epic-comments">${commentsSection(epic.comments, canComment)}</div>
  `;

  if (!readOnly) {
    content.querySelector('#edit-epic')!.addEventListener('click', () => {
      openEditEpicModal(project.id, epic, reload);
    });
    content.querySelector('#delete-epic')!.addEventListener('click', async () => {
      if (!confirm('Delete this epic? Its tasks will be kept but moved out of the epic.')) return;
      try {
        await api.del(`/projects/${project.id}/epics/${epic.id}`);
        toast('Epic deleted');
        ctx.goEpics();
      } catch (e: any) {
        toast(e.message || 'Could not delete epic', 'error');
      }
    });
    content.querySelector('#add-task')!.addEventListener('click', () => {
      openTaskForm(project, {
        epicId: epic.id,
        epics: ctx.epics,
        tasks: ctx.tasks,
        onChanged: reload,
      });
    });
  }

  wireEpicTaskList(content.querySelector('#epic-task-list') as HTMLElement, ctx);

  content.querySelector('#epic-comment-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const body = String(data.get('body') || '').trim();
    if (!body) return;
    const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
    submitBtn.disabled = true;
    submitBtn.textContent = 'Posting…';
    try {
      await api.post<{ epic: Epic }>(
        `/projects/${project.id}/epics/${epic.id}/comments`,
        { body }
      );
      toast('Comment added');
      await renderEpicDetail(content, ctx, epic.id);
    } catch (err: any) {
      toast(err.message || 'Could not add comment', 'error');
      submitBtn.disabled = false;
      submitBtn.textContent = 'Add comment';
    }
  });

  // Charts
  if (Chart.defaults) {
    Chart.defaults.font.family = "'Inter', sans-serif";
    Chart.defaults.color = '#6b7280';
  }

  const completionCanvas = content.querySelector('#epic-completion-chart') as HTMLCanvasElement;
  if (completionCanvas) {
    new Chart(completionCanvas, {
      type: 'doughnut',
      data: {
        labels: ['Completed', 'Remaining'],
        datasets: [
          {
            data: [completed, Math.max(0, total - completed)],
            backgroundColor: ['#16a34a', '#e5e8ee'],
            borderWidth: 2,
            borderColor: '#ffffff',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '62%',
        plugins: { legend: { position: 'bottom' } },
      },
    });
  }

  const statusCanvas = content.querySelector('#epic-status-chart') as HTMLCanvasElement;
  if (statusCanvas) {
    const statuses = ['not_started', 'started', 'in_progress', 'blocked', 'complete'] as const;
    new Chart(statusCanvas, {
      type: 'bar',
      data: {
        labels: statuses.map((s) => statusMeta(s).label),
        datasets: [
          {
            label: 'Tasks',
            data: statuses.map((s) => epicTasks.filter((t) => t.status === s).length),
            backgroundColor: statuses.map((s) => statusMeta(s).color),
            borderRadius: 8,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
      },
    });
  }
}

function renderEpicTasks(epicTasks: TaskItem[], ctx: ProjectContext): string {
  if (epicTasks.length === 0) {
    return `<div class="empty-state"><h3>No tasks in this epic yet</h3><p class="muted">Create a task to start working toward this goal.</p></div>`;
  }
  const flat = flattenTree(epicTasks);
  return `
    <div class="task-list">
      ${flat
        .map(({ task, depth }) => {
          const meta = statusMeta(task.status);
          return `
          <div class="task-row" data-id="${task.id}" style="grid-template-columns:1fr 150px 90px 140px;">
            <div class="task-row-title" style="padding-left:${depth * 22}px;">
              ${depth > 0 ? '<span class="muted">↳ </span>' : ''}${escapeHtml(task.title)}
            </div>
            <span class="assignee">${escapeHtml(task.assigneeName || 'Unassigned')}</span>
            <span class="points-chip">${task.storyPoints} pts</span>
            <span class="badge" style="color:${meta.color};background:${meta.bg};justify-self:start;">
              <span class="dot"></span>${meta.label}
            </span>
          </div>
        `;
        })
        .join('')}
    </div>
  `;
}

function wireEpicTaskList(listEl: HTMLElement, ctx: ProjectContext): void {
  listEl.querySelectorAll<HTMLElement>('.task-row').forEach((row) => {
    row.addEventListener('click', () => {
      const task = ctx.tasks.find((t) => t.id === row.dataset.id!);
      if (task) {
        openTaskModal(
          task,
          ctx.project,
          ctx.tasks,
          ctx.epics,
          ctx.reload,
          ctx.readOnly,
          ctx.canComment
        );
      }
    });
  });
}

function openEditEpicModal(projectId: string, epic: Epic, onChanged: () => void): void {
  openModal(
    `
    <h2 style="font-size:20px;margin-bottom:16px;">Edit epic</h2>
    <form id="edit-epic-form">
      <div class="field">
        <label>Epic name</label>
        <input class="input" name="name" value="${escapeHtml(epic.name)}" />
      </div>
      <div class="field">
        <label>Description (Markdown supported)</label>
        <textarea class="textarea" name="description" rows="6">${escapeHtml(
          epic.description
        )}</textarea>
      </div>
      <div class="form-error hidden" id="epic-edit-error"></div>
      <div style="display:flex;gap:10px;justify-content:flex-end;">
        <button type="button" class="btn btn-ghost" data-close>Cancel</button>
        <button type="submit" class="btn btn-primary">Save changes</button>
      </div>
    </form>
  `,
    (body, close) => {
      const form = body.querySelector('#edit-epic-form') as HTMLFormElement;
      const errorEl = body.querySelector('#epic-edit-error') as HTMLElement;
      body.querySelector('[data-close]')!.addEventListener('click', close);

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        errorEl.classList.add('hidden');
        const data = new FormData(form);
        const name = String(data.get('name') || '').trim();
        if (!name) {
          errorEl.textContent = 'Epic name is required';
          errorEl.classList.remove('hidden');
          return;
        }
        try {
          await api.patch(`/projects/${projectId}/epics/${epic.id}`, {
            name,
            description: String(data.get('description') || ''),
          });
          toast('Epic updated');
          close();
          onChanged();
        } catch (e: any) {
          errorEl.textContent = e.message || 'Could not update epic';
          errorEl.classList.remove('hidden');
        }
      });
    }
  );
}

function commentsSection(
  comments: TaskComment[] | undefined,
  canComment: boolean
): string {
  const list = comments || [];
  return `
    <div style="margin-top:32px;border-top:1px solid var(--border);padding-top:20px;">
      <h3 style="font-size:16px;font-weight:700;margin:0 0 12px;">Comments (${list.length})</h3>
      ${
        list.length === 0
          ? '<p class="muted">No comments yet.</p>'
          : list
              .map(
                (c) => `
          <div class="comment">
            <div class="comment-head">
              <span class="avatar">${initials(c.authorName)}</span>
              <strong>${escapeHtml(c.authorName)}</strong>
              <span class="muted" style="font-size:12px;">${formatDate(c.createdAt)}</span>
            </div>
            <div class="markdown-body comment-body">${renderMarkdown(c.body)}</div>
          </div>
        `
              )
              .join('')
      }
      ${
        canComment
          ? `
        <form id="epic-comment-form" style="margin-top:14px;">
          <textarea class="textarea" name="body" rows="3" placeholder="Add a comment… (Markdown supported)"></textarea>
          <div style="display:flex;justify-content:flex-end;margin-top:10px;">
            <button class="btn btn-primary btn-sm" type="submit">Add comment</button>
          </div>
        </form>
      `
          : ''
      }
    </div>
  `;
}
